import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "./../../components/panel/panel.jsx";
import { useState, useEffect, useCallback } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import CommonTimeSlotForAllWeek from "../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek.js";
import ScheduleManager from "../../components/CustomComponents/TimeSchedule/weeklySlots.js";
import schedule from "../../components/CustomComponents/TimeSchedule/schedule.js";
import moment from "moment";
import {
  checkIfCommonTimeSlots,
  convertToScheduleFormat,
} from "../../components/CustomComponents/TimeSchedule/scheduleConv.js";
import { useSelector } from "react-redux";
import UpdateShimmer from "../../components/CustomComponents/preLoad/UpdateShimmer.jsx";

const UpdateStaffSchedule = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { UserName } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [regularHours, setRegularHours] = useState(true);
  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [commonTimeSlot, setCommonTimeSlot] = useState([
    {
      from: "00:00",
      to: "23:59",
    },
  ]);
  const [commonBreak, setCommonBreak] = useState(null);
  const [schedules, setSchedules] = useState(schedule);
  const [initialLoading, setInitialLoading] = useState(true);
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  const isDarkMode = useDarkMode();

  const FetchStaffSchedule = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_STAFF_DATA, {
        params: {
          CompID,
          UserName,
        },
      });
      const staff = response.data.data || response.data;

      console.log("Loaded staff weeklySchedule:", staff.weeklySchedule);

      if (
        staff.weeklySchedule &&
        Array.isArray(staff.weeklySchedule) &&
        staff.weeklySchedule.length > 0
      ) {
        setRegularHours(false);
        const isCommon = checkIfCommonTimeSlots(staff.weeklySchedule);
        if (isCommon) {
          const firstDaySlots = staff.weeklySchedule
            .filter((day) => day.dayName === staff.weeklySchedule[0].dayName)
            .map((day) => ({
              from: day.openTime,  // Keep as "HH:mm"
              to: day.closeTime,   // Keep as "HH:mm"
            }));
          console.log("Set commonTimeSlot:", firstDaySlots);
          setCommonTimeSlot(firstDaySlots);

          // Load common break from first schedule
          const firstSchedule = staff.weeklySchedule[0];
          if (firstSchedule.startBraekTime || firstSchedule.breakMinutes !== null && firstSchedule.breakMinutes !== undefined) {
            setCommonBreak({
              startBraekTime: firstSchedule.startBraekTime || "",
              breakMinutes: firstSchedule.breakMinutes || null,
              isBreakInclusive: firstSchedule.isBreakInclusive || false
            });
          }
        } else {
          setUseSameTimeSlots(false);
          let convertedSchedules = convertToScheduleFormat(
            staff.weeklySchedule
          );

          // Map break data to each slot (assuming one slot per day)
          convertedSchedules = convertedSchedules.map((sch) => {
            const dayEntry = staff.weeklySchedule.find(d => d.dayName === sch.day);
            if (dayEntry && sch.timeSlots.length > 0) {
              sch.timeSlots[0].break = (dayEntry.startBraekTime || dayEntry.breakMinutes !== null && dayEntry.breakMinutes !== undefined) ? {
                startBraekTime: dayEntry.startBraekTime || "",
                breakMinutes: dayEntry.breakMinutes || null,
                isBreakInclusive: dayEntry.isBreakInclusive || false
              } : null;
            }
            return sch;
          });

          setSchedules(convertedSchedules);
        }
      } else {
        // Ensure default schedules have break: null
        const defaultSchedulesWithBreaks = schedule.map(sch => ({
          ...sch,
          timeSlots: sch.timeSlots.map(slot => ({ ...slot, break: null }))
        }));
        setSchedules(defaultSchedulesWithBreaks);
      }
    } catch (err) {
      showErrorToast("Failed to fetch staff schedule data");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [CompID, UserName]);

  useEffect(() => {
    FetchStaffSchedule();
  }, [FetchStaffSchedule]);

  const applyCommonTimeSlotToAll = () => {
    const updatedSchedules = schedules.map((schedule) => ({
      ...schedule,
      timeSlots: commonTimeSlot.map((slot) => ({ 
        ...slot, 
        break: commonBreak ? { 
          startBraekTime: commonBreak.startBraekTime, 
          breakMinutes: commonBreak.breakMinutes,
          isBreakInclusive: commonBreak.isBreakInclusive || false
        } : null 
      })),
    }));
    setSchedules(updatedSchedules);
  };

  const handleSubmit = async (e) => {
    debugger;
    if (e) e.preventDefault();
    if (!UserName || UserName.trim().length === 0) {
      console.error("Invalid username");
      showErrorToast("Invalid username");
      return;
    }

    if (!CompID) {
      console.error("CompID is missing");
      showErrorToast("Company ID is missing");
      return;
    }

    try {
      setLoading(true);
      console.log("Submit started", {
        CompID,
        UserName,
        regularHours,
        useSameTimeSlots,
      });

      const formattedSchedules = [];

      // Only add schedules if NOT using regular hours
      if (!regularHours) {
        if (useSameTimeSlots) {
          const days = [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ];

          if (commonTimeSlot.length > 0) {
            commonTimeSlot.forEach((slot) => {
              days.forEach((day) => {
                formattedSchedules.push({
                  id: 0,
                  compID: CompID,
                  userName: UserName,
                  dayName: day,
                  openTime: slot.from ? moment(slot.from, "HH:mm").format("HH:mm") : null,
                  closeTime: slot.to ? moment(slot.to, "HH:mm").format("HH:mm") : null,
                  startBraekTime: commonBreak?.startBraekTime ? commonBreak.startBraekTime : null,
                  endBraekTime: commonBreak?.startBraekTime && commonBreak.breakMinutes ? moment(commonBreak.startBraekTime, "HH:mm").add(commonBreak.breakMinutes, "minutes").format("HH:mm") : null,
                  breakMinutes: commonBreak?.breakMinutes || null,
                  isBreakInclusive: commonBreak?.isBreakInclusive || false,
                  updateDateTime: new Date().toISOString(),
                });
              });
            });
          }
        } else {
          schedules.forEach((schedule) => {
            if (schedule.checked && schedule.timeSlots.length > 0) {
              schedule.timeSlots.forEach((slot) => {
                if (
                  moment(slot.from, "HH:mm").isValid() &&
                  moment(slot.to, "HH:mm").isValid()
                ) {
                  formattedSchedules.push({
                    id: 0,
                    compID: CompID,
                    userName: UserName,
                    dayName: schedule.day,
                    openTime: moment(slot.from, "HH:mm").format("HH:mm"),
                    closeTime: moment(slot.to, "HH:mm").format("HH:mm"),
                    startBraekTime: slot.break?.startBraekTime || null,
                    endBraekTime: slot.break?.startBraekTime && slot.break.breakMinutes ? moment(slot.break.startBraekTime, "HH:mm").add(slot.break.breakMinutes, "minutes").format("HH:mm") : null,
                    breakMinutes: slot.break?.breakMinutes || null,
                    isBreakInclusive: slot.break?.isBreakInclusive || false,
                    updateDateTime: new Date().toISOString(),
                  });
                }
              });
            }
          });
        }
      }

      console.log("Formatted Schedules to send:", formattedSchedules);
      console.log("Number of schedules:", formattedSchedules.length);
      console.log("Request params:", { UserName, CompID });
      console.log("API URL:", APIs.UPDATE_USER_WEEKLY_SCHEDULE);

      // Check if API constant exists
      if (!APIs.UPDATE_USER_WEEKLY_SCHEDULE) {
        console.error("API URL is undefined!");
        showErrorToast("API configuration error");
        setLoading(false);
        return;
      }

      console.log("Sending PUT request...");

      const response = await axiosInstance.put(
        APIs.UPDATE_USER_WEEKLY_SCHEDULE,
        formattedSchedules,
        {
          params: {
            UserName,
            CompID,
          },
        }
      );



      console.log("Response received:", response.data);

      if (response.data.status) {
        showSuccessToast("Staff schedule updated successfully.");
        // Wait a bit before navigating to allow the user to see the success message
        setTimeout(() => {
          navigate("/team/manageTeam");
        }, 1000);
      } else {
        showErrorToast(response.data.message);
      }
    } catch (err) {
      console.error("=== ERROR OCCURRED ===");
      console.error("Error object:", err);
      console.error("Error response:", err.response);
      console.error("Error message:", err.message);
      console.error("Error config:", err.config);

      if (err.response) {
        // Server responded with error
        console.error("Server error status:", err.response.status);
        console.error("Server error data:", err.response.data);
        showErrorToast(err.response?.data?.message || "Server error occurred");
      } else if (err.request) {
        // Request made but no response
        console.error("No response received:", err.request);
        showErrorToast("No response from server. Check network connection.");
      } else {
        // Error in request setup
        console.error("Request setup error:", err.message);
        showErrorToast("Error setting up request: " + err.message);
      }
    } finally {
      console.log("=== REQUEST COMPLETED ===");
      setLoading(false);
    }
  }

  return (
    <div>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-2 mt-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/team/manageTeam">Manage Staff</Link>
            </li>
            <li className="breadcrumb-item active">Update Schedule</li>
          </ol>
          <h1 className="page-header mb-0">Update Schedule ({UserName})</h1>
        </div>
        <div className="ms-auto">
          <button
            onClick={handleSubmit}
            className="btn px-4"
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fa fa-save fa-lg me-2 ms-n2 text-white"></i>
            Submit
          </button>
        </div>
      </div>

      <div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>Staff Schedule</PanelHeader>
          <PanelBody>
            {initialLoading ? (
              <UpdateShimmer />
            ) : (
              <>
                <div className="row mb-3">
                  <div className="col-8">
                    <ToggleSwitch
                      isChecked={regularHours}
                      onToggle={(checked) => setRegularHours(checked)}
                      label={"Official Business Hours"}
                      disabled={false}
                      themeColor={themeColor}
                    />
                  </div>
                </div>
                {!regularHours && (
                  <div className="row m-3">
                    <div className="mb-3">
                      <ToggleSwitch
                        isChecked={useSameTimeSlots}
                        onToggle={(checked) => {
                          setUseSameTimeSlots(checked);
                          if (!checked) {
                            applyCommonTimeSlotToAll();
                          }
                        }}
                        label={"Use same time slots for all days"}
                        disabled={false}
                        themeColor={themeColor}
                      />
                    </div>
                    {useSameTimeSlots ? (
                      <CommonTimeSlotForAllWeek
                        setCommonTimeSlots={setCommonTimeSlot}
                        commonTimeSlots={commonTimeSlot}
                        isStaffSchedule={true}
                        commonBreak={commonBreak}
                        setCommonBreak={setCommonBreak}
                      />
                    ) : (
                      <ScheduleManager
                        schedules={schedules}
                        setSchedules={setSchedules}
                        themeColor={themeColor}
                        isDarkMode={isDarkMode}
                        isStaffSchedule={true}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </PanelBody>
        </Panel>
      </div>
    </div>
  );
};

export default UpdateStaffSchedule;